/**
 * Timing Engine
 *
 * Maintains synchronized time between audio and visual systems
 * Provides high-precision timing for accurate note detection
 */

export class TimingEngine {
  private audioContext: AudioContext | null = null;
  private songStartTime: number = 0;
  private isPaused: boolean = true;
  private pauseTime: number = 0;
  private totalPauseTime: number = 0;

  // Offsets for calibration (in milliseconds)
  private audioOffset: number = 0;
  private visualOffset: number = 0;

  // Approach time - how long notes are visible before hit (in ms)
  private readonly APPROACH_TIME: number = 1000;

  constructor() {
    this.initAudioContext();
  }

  private initAudioContext(): void {
    try {
      this.audioContext = new AudioContext();
      console.log('AudioContext initialized');
    } catch (error) {
      console.error('Failed to initialize AudioContext:', error);
    }
  }

  /**
   * Start timing from the beginning of a song
   */
  start(): void {
    if (!this.audioContext) {
      console.error('AudioContext not initialized');
      return;
    }

    this.songStartTime = this.audioContext.currentTime;
    this.totalPauseTime = 0;
    this.isPaused = false;
    console.log('Timing started');
  }

  /**
   * Pause timing
   */
  pause(): void {
    if (this.isPaused || !this.audioContext) return;

    this.pauseTime = this.audioContext.currentTime;
    this.isPaused = true;
  }

  /**
   * Resume timing after pause
   */
  resume(): void {
    if (!this.isPaused || !this.audioContext) return;

    this.totalPauseTime += this.audioContext.currentTime - this.pauseTime;
    this.isPaused = false;
  }

  /**
   * Get current time relative to song start (in milliseconds)
   * This is the "song time" used for note hit detection
   */
  getCurrentTime(): number {
    if (!this.audioContext) return 0;
    if (this.isPaused) return (this.pauseTime - this.songStartTime - this.totalPauseTime) * 1000;

    const rawTime = (this.audioContext.currentTime - this.songStartTime - this.totalPauseTime) * 1000;
    return rawTime - this.audioOffset;
  }

  /**
   * Get visual time - when to spawn notes
   * Notes should be spawned APPROACH_TIME milliseconds before they need to be hit
   */
  getVisualTime(): number {
    return this.getCurrentTime() + this.APPROACH_TIME + this.visualOffset;
  }

  /**
   * Set audio offset for calibration
   */
  setAudioOffset(offsetMs: number): void {
    this.audioOffset = offsetMs;
  }

  /**
   * Set visual offset for calibration
   */
  setVisualOffset(offsetMs: number): void {
    this.visualOffset = offsetMs;
  }

  /**
   * Get the AudioContext for audio playback
   */
  getAudioContext(): AudioContext | null {
    return this.audioContext;
  }

  /**
   * Get approach time
   */
  getApproachTime(): number {
    return this.APPROACH_TIME;
  }

  /**
   * Reset timing engine
   */
  reset(): void {
    this.songStartTime = 0;
    this.totalPauseTime = 0;
    this.pauseTime = 0;
    this.isPaused = true;
  }
}
